import { useState, useRef, useEffect } from 'react';
import {
  Settings,
  PanelLeft,
  Palette,
  Globe,
  Type,
  Bell,
  Database,
  User,
  LogOut,
  Download,
  Upload,
  Key,
  Plus,
  Trash2,
  Copy,
  Check,
  Smartphone,
} from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { useToast } from '../contexts/ToastContext';

const TIMEZONE_OPTIONS = [
  { value: 'auto', label: 'Auto-detect (browser)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European (CET)' },
  { value: 'Europe/Helsinki', label: 'Eastern European (EET)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
];

function Section({ icon: Icon, title, children }: { icon: typeof Settings; title: string; children: React.ReactNode }) {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-bg-secondary border-b border-border">
        <Icon size={16} className="text-accent" />
        <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
      </div>
      <div className="px-4 py-3 space-y-4">
        {children}
      </div>
    </div>
  );
}

function SettingRow({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-text-primary">{label}</div>
        {description && <div className="text-xs text-text-muted mt-0.5">{description}</div>}
      </div>
      <div className="flex-shrink-0">
        {children}
      </div>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
        checked ? 'bg-accent' : 'bg-bg-hover'
      }`}
    >
      <div
        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-bg-surface shadow-sm transition-transform duration-200 ${
          checked ? 'translate-x-5' : ''
        }`}
      />
    </button>
  );
}

function Select({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="px-2 py-1.5 bg-bg-input border border-border rounded-md text-sm text-text-primary focus:outline-none focus:border-accent"
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function SettingsPage() {
  const { user, logout } = useAuth();
  const { prefs, update } = usePreferences();
  const toast = useToast();
  const [signingOut, setSigningOut] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleChange(changes: Record<string, string>) {
    try {
      await update(changes);
    } catch {
      toast.error('Failed to save setting');
    }
  }

  async function handleExport() {
    setExporting(true);
    try {
      const data = await apiGet<object>('/settings/export');
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dossier-export-${new Date().toISOString().slice(0, 10)}.dossier`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Data exported');
    } catch {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  }

  async function handleImport(file: File) {
    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (data.format !== 'dossier-export') {
        toast.error('Invalid .dossier file');
        return;
      }
      const res = await fetch('/api/settings/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await (await import('../lib/firebase')).getIdToken()}`,
        },
        body: text,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(err.error);
      }
      const result = await res.json();
      const total = Object.values(result.counts as Record<string, number>).reduce((a: number, b: number) => a + b, 0);
      toast.success(`Imported ${total} items`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to import data');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await logout();
    } catch {
      toast.error('Failed to sign out');
      setSigningOut(false);
    }
  }

  function handleClearRecent() {
    localStorage.removeItem('dossier-recent-items');
    toast.success('Recent items cleared');
  }

  function handleClearSearchHistory() {
    localStorage.removeItem('dossier-recent-searches');
    toast.success('Search history cleared');
  }

  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="flex items-center gap-3 mb-6">
          <Settings size={24} className="text-accent" />
          <h1 className="text-2xl font-bold text-text-primary">Settings</h1>
        </div>

        {/* Sidebar / Navigation */}
        <Section icon={PanelLeft} title="Sidebar & Navigation">
          <SettingRow label="Show pinned items" description="Display pinned items in the sidebar for quick access">
            <Toggle
              checked={prefs['sidebar.pinnedItems'] !== 'false'}
              onChange={v => handleChange({ 'sidebar.pinnedItems': v ? 'true' : 'false' })}
            />
          </SettingRow>
          <SettingRow label="Show recent items" description="Display recently visited items below the main menu">
            <Toggle
              checked={prefs['sidebar.recentItems'] !== 'false'}
              onChange={v => handleChange({ 'sidebar.recentItems': v ? 'true' : 'false' })}
            />
          </SettingRow>
          <SettingRow label="Recent items count" description="Number of recent items to show (3-10)">
            <Select
              value={prefs['sidebar.recentItemsCount'] || '5'}
              onChange={v => handleChange({ 'sidebar.recentItemsCount': v })}
              options={[
                { value: '3', label: '3' },
                { value: '5', label: '5' },
                { value: '7', label: '7' },
                { value: '10', label: '10' },
              ]}
            />
          </SettingRow>
          <SettingRow label="Sidebar open by default" description="Start with sidebar expanded on desktop">
            <Toggle
              checked={prefs['sidebar.defaultOpen'] !== 'false'}
              onChange={v => handleChange({ 'sidebar.defaultOpen': v ? 'true' : 'false' })}
            />
          </SettingRow>
          <SettingRow label="Show active meetings" description="Display current/upcoming meetings in the sidebar">
            <Toggle
              checked={prefs['sidebar.activeMeetings'] !== 'false'}
              onChange={v => handleChange({ 'sidebar.activeMeetings': v ? 'true' : 'false' })}
            />
          </SettingRow>
          <SettingRow label="Show calendar widget" description="Display a compact calendar in the sidebar">
            <Toggle
              checked={prefs['sidebar.calendarWidget'] !== 'false'}
              onChange={v => handleChange({ 'sidebar.calendarWidget': v ? 'true' : 'false' })}
            />
          </SettingRow>
          <SettingRow label="First day of week" description="Which day starts the week in the calendar">
            <Select
              value={prefs['sidebar.weekStartDay'] || '0'}
              onChange={v => handleChange({ 'sidebar.weekStartDay': v })}
              options={[
                { value: '0', label: 'Sunday' },
                { value: '1', label: 'Monday' },
              ]}
            />
          </SettingRow>
          {prefs['sidebar.activeMeetings'] !== 'false' && (
            <SettingRow label="Show meetings early" description="Minutes before start time to show in sidebar">
              <Select
                value={prefs['sidebar.activeMeetingsEarlyMinutes'] || '30'}
                onChange={v => handleChange({ 'sidebar.activeMeetingsEarlyMinutes': v })}
                options={[
                  { value: '0', label: 'At start time' },
                  { value: '5', label: '5 min before' },
                  { value: '10', label: '10 min before' },
                  { value: '15', label: '15 min before' },
                  { value: '30', label: '30 min before' },
                  { value: '60', label: '1 hour before' },
                ]}
              />
            </SettingRow>
          )}
        </Section>

        {/* Appearance */}
        <Section icon={Palette} title="Appearance">
          <SettingRow label="Theme" description="Switch between dark and light mode">
            <Select
              value={prefs['appearance.theme'] || 'dark'}
              onChange={v => handleChange({ 'appearance.theme': v })}
              options={[
                { value: 'dark', label: 'Dark' },
                { value: 'light', label: 'Light' },
              ]}
            />
          </SettingRow>
        </Section>

        {/* Timezone */}
        <Section icon={Globe} title="Timezone">
          <SettingRow
            label="Timezone"
            description={prefs['timezone'] === 'auto' ? `Auto-detected: ${detectedTimezone}` : undefined}
          >
            <Select
              value={prefs['timezone'] || 'auto'}
              onChange={v => handleChange({ 'timezone': v })}
              options={TIMEZONE_OPTIONS}
            />
          </SettingRow>
        </Section>

        {/* Editor */}
        <Section icon={Type} title="Editor">
          <SettingRow label="Show toolbar by default" description="Display the formatting toolbar when opening an editor">
            <Toggle
              checked={prefs['editor.toolbarVisible'] !== 'false'}
              onChange={v => handleChange({ 'editor.toolbarVisible': v ? 'true' : 'false' })}
            />
          </SettingRow>
          <SettingRow label="Auto-save delay" description="How long to wait after typing before saving">
            <Select
              value={prefs['editor.autosaveDelay'] || '2000'}
              onChange={v => handleChange({ 'editor.autosaveDelay': v })}
              options={[
                { value: '1000', label: '1 second' },
                { value: '2000', label: '2 seconds' },
                { value: '5000', label: '5 seconds' },
              ]}
            />
          </SettingRow>
        </Section>

        {/* Notifications */}
        <Section icon={Bell} title="Notifications">
          <SettingRow label="Toast duration" description="How long notifications stay visible">
            <Select
              value={prefs['toast.duration'] || '3000'}
              onChange={v => handleChange({ 'toast.duration': v })}
              options={[
                { value: '2000', label: 'Short (2s)' },
                { value: '3000', label: 'Normal (3s)' },
                { value: '5000', label: 'Long (5s)' },
              ]}
            />
          </SettingRow>
          <SettingRow label="Toast position" description="Where notifications appear on screen">
            <Select
              value={prefs['toast.position'] || 'bottom-right'}
              onChange={v => handleChange({ 'toast.position': v })}
              options={[
                { value: 'bottom-right', label: 'Bottom right' },
                { value: 'top-right', label: 'Top right' },
                { value: 'bottom-center', label: 'Bottom center' },
              ]}
            />
          </SettingRow>
        </Section>

        {/* Data */}
        <Section icon={Database} title="Data">
          <SettingRow label="Export data" description="Download all your data as a .dossier file">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-accent/10 text-accent hover:bg-accent/20 transition-colors disabled:opacity-50"
            >
              <Download size={12} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </SettingRow>
          <SettingRow label="Import data" description="Restore from a .dossier file (merges with existing data)">
            <input
              ref={fileInputRef}
              type="file"
              accept=".dossier"
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) handleImport(file);
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-bg-hover text-text-secondary hover:text-text-primary hover:bg-bg-input transition-colors disabled:opacity-50"
            >
              <Upload size={12} />
              {importing ? 'Importing...' : 'Import'}
            </button>
          </SettingRow>
          <SettingRow label="Recent items" description="Clear the recently visited items list in the sidebar">
            <button
              onClick={handleClearRecent}
              className="px-3 py-1.5 text-xs rounded-md bg-bg-hover text-text-secondary hover:text-text-primary hover:bg-bg-input transition-colors"
            >
              Clear
            </button>
          </SettingRow>
          <SettingRow label="Search history" description="Clear saved recent searches from the command palette">
            <button
              onClick={handleClearSearchHistory}
              className="px-3 py-1.5 text-xs rounded-md bg-bg-hover text-text-secondary hover:text-text-primary hover:bg-bg-input transition-colors"
            >
              Clear
            </button>
          </SettingRow>
        </Section>

        {/* API Keys */}
        <ApiKeysSection />

        {/* Account */}
        <Section icon={User} title="Account">
          <SettingRow label="Email" description={user?.email || 'Not signed in'}>
            <span className="text-xs text-text-muted">{user?.displayName}</span>
          </SettingRow>
          <SettingRow label="Sign out" description="Sign out of Dossier">
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-danger/10 text-danger hover:bg-danger/20 transition-colors disabled:opacity-50"
            >
              <LogOut size={12} />
              {signingOut ? 'Signing out...' : 'Sign out'}
            </button>
          </SettingRow>
        </Section>
      </div>
    </div>
  );
}

interface ApiKeyRecord {
  id: string;
  name: string;
  key_prefix: string;
  key?: string;
  created_at: string;
  last_used_at: string | null;
}

function ApiKeysSection() {
  const toast = useToast();
  const [keys, setKeys] = useState<ApiKeyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    apiGet<ApiKeyRecord[]>('/settings/api-keys')
      .then(setKeys)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  async function handleCreate() {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const result = await apiPost<ApiKeyRecord & { key: string }>('/settings/api-keys', { name: newName.trim() });
      setNewKey(result.key);
      setKeys(prev => [result, ...prev]);
      setNewName('');
      toast.success('API key created');
    } catch (err) {
      console.error('Failed to create API key:', err);
      toast.error('Failed to create API key');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Revoke this API key? Any shortcuts using it will stop working.')) return;
    try {
      await apiDelete(`/settings/api-keys/${id}`);
      setKeys(prev => prev.filter(k => k.id !== id));
      toast.success('API key revoked');
    } catch (err) {
      console.error('Failed to delete API key:', err);
      toast.error('Failed to revoke API key');
    }
  }

  function handleCopy(text: string) {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const [showSetup, setShowSetup] = useState(false);

  function copyField(text: string) {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  }

  return (
    <Section icon={Key} title="API Keys">
      <p className="text-xs text-text-muted mb-3">
        Create API keys for external integrations like iOS Shortcuts. Keys are shown once on creation.
      </p>

      {newKey && (
        <div className="mb-3 p-3 bg-success/10 border border-success/30 rounded-lg">
          <p className="text-xs text-success font-medium mb-1">Your new API key (only shown once):</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-bg-primary text-text-primary px-2 py-1.5 rounded font-mono break-all select-all">
              {newKey}
            </code>
            <button
              onClick={() => handleCopy(newKey)}
              className="flex-shrink-0 p-1.5 rounded-md bg-success/20 text-success hover:bg-success/30 transition-colors"
              title="Copy to clipboard"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => setShowSetup(!showSetup)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-accent text-white hover:bg-accent-hover transition-colors"
            >
              <Smartphone size={12} />
              {showSetup ? 'Hide Setup Guide' : 'Setup iOS Shortcut'}
            </button>
            <button
              onClick={() => { setNewKey(null); setShowSetup(false); }}
              className="text-[10px] text-text-muted hover:text-text-secondary transition-colors"
            >
              Dismiss
            </button>
          </div>

          {showSetup && (
            <div className="mt-3 p-3 bg-bg-card border border-border rounded-lg space-y-4">
              <p className="text-xs font-medium text-text-primary">Open Shortcuts on your iPhone and create a new shortcut:</p>

              {/* Step 1 */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold text-accent bg-accent/15 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">1</span>
                  <p className="text-xs text-text-primary font-medium">Ask for Input</p>
                </div>
                <p className="text-[10px] text-text-muted ml-7">Type: Text, Prompt: "Quick note"</p>
              </div>

              {/* Step 2 */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold text-accent bg-accent/15 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">2</span>
                  <p className="text-xs text-text-primary font-medium">Get Contents of URL</p>
                </div>
                <div className="ml-7 space-y-2">
                  {/* URL */}
                  <div>
                    <p className="text-[10px] text-text-muted mb-1">Paste this URL:</p>
                    <button
                      onClick={() => copyField('https://dossier.kandyfamily.com/api/external/quick-capture')}
                      className="w-full text-left px-3 py-2 bg-bg-primary rounded-md border border-border hover:border-accent transition-colors active:bg-accent/10"
                    >
                      <code className="text-[11px] text-text-primary font-mono break-all">https://dossier.kandyfamily.com/api/external/quick-capture</code>
                      <span className="flex items-center gap-1 text-[10px] text-accent mt-1"><Copy size={10} /> Tap to copy</span>
                    </button>
                  </div>

                  <p className="text-[10px] text-text-muted">Set Method: <strong className="text-text-primary">POST</strong></p>

                  {/* Header */}
                  <div>
                    <p className="text-[10px] text-text-muted mb-1">Add Header — Key: <strong className="text-text-primary">X-API-Key</strong>, Value:</p>
                    <button
                      onClick={() => copyField(newKey!)}
                      className="w-full text-left px-3 py-2 bg-bg-primary rounded-md border border-border hover:border-accent transition-colors active:bg-accent/10"
                    >
                      <code className="text-[11px] text-text-primary font-mono break-all">{newKey}</code>
                      <span className="flex items-center gap-1 text-[10px] text-accent mt-1"><Copy size={10} /> Tap to copy</span>
                    </button>
                  </div>

                  {/* Body */}
                  <p className="text-[10px] text-text-muted">Request Body: <strong className="text-text-primary">JSON</strong> — add key <strong className="text-text-primary">text</strong> with value set to the <em className="text-accent">Provided Input</em> variable</p>
                </div>
              </div>

              <p className="text-[10px] text-text-muted italic ml-7">Tip: Add to Home Screen for one-tap access, or say "Hey Siri, [shortcut name]"</p>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <input
          type="text"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder="Key name (e.g., iPhone Shortcut)"
          onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
          className="flex-1 px-2.5 py-1.5 text-xs bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
        />
        <button
          onClick={handleCreate}
          disabled={creating || !newName.trim()}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50"
        >
          <Plus size={12} />
          Create
        </button>
      </div>

      {loading ? (
        <p className="text-xs text-text-muted">Loading...</p>
      ) : keys.length === 0 ? (
        <p className="text-xs text-text-muted">No API keys yet.</p>
      ) : (
        <div className="space-y-1.5">
          {keys.map(k => (
            <div key={k.id} className="flex items-center gap-2 px-2 py-1.5 bg-bg-input rounded-md group">
              <Key size={12} className="text-text-muted flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-xs text-text-primary font-medium block">{k.name}</span>
                <span className="text-[10px] text-text-muted">
                  {k.key_prefix}... · Created {new Date(k.created_at).toLocaleDateString()}
                  {k.last_used_at && ` · Last used ${new Date(k.last_used_at).toLocaleDateString()}`}
                </span>
              </div>
              <button
                onClick={() => handleDelete(k.id)}
                className="opacity-0 group-hover:opacity-100 p-1 text-text-muted hover:text-danger transition-all flex-shrink-0"
                title="Revoke key"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}
